interface ILruCacheOptions {
  maxEntries: number;
}

interface ILruCache<Key, Value> {
  get: (key: Key) => Value | undefined;
  set: (key: Key, value: Value) => void;
  clear: () => void;
}

/**
 * In-memory LRU-ish cache backed by insertion-ordered Map entries.
 * `get` moves a hit to newest; at capacity, `set` drops the oldest key.
 *
 * @param options - Cache configuration.
 * @param options.maxEntries - Max stored entries; `set` is a no-op when this is ≤ 0.
 * @returns Object with `get`, `set`, and `clear`.
 */
export const createLruCache = <Key, Value>({
  maxEntries,
}: ILruCacheOptions): ILruCache<Key, Value> => {
  const cache = new Map<Key, Value>();

  return {
    get: (key) => {
      if (!cache.has(key)) return undefined;

      const value = cache.get(key) as Value;
      cache.delete(key);
      cache.set(key, value);
      return value;
    },
    set: (key, value) => {
      if (maxEntries <= 0) return;

      if (cache.has(key)) {
        cache.delete(key);
      } else if (cache.size >= maxEntries) {
        const oldestEntry = cache.keys().next();
        if (!oldestEntry.done) cache.delete(oldestEntry.value);
      }

      cache.set(key, value);
    },
    clear: () => {
      cache.clear();
    },
  };
};
